if (!customElements.get('wlsn-celebration-hero')) {
  class WLSNCelebrationHero extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        card: 'js-wlsn-celebration-hero-card',
        confetti: 'js-wlsn-celebration-hero-confetti',
        counter: 'js-wlsn-celebration-hero-counter',
        slot: 'js-wlsn-celebration-hero-slot',
        badge: 'js-wlsn-celebration-hero-badge',
        animate: 'is-animated',
        inView: 'is-in-view',
        flip: 'is-flipped',
        pop: 'is-popped',
      };

      this.timers = [];
      this.hasStarted = false;
    }

    connectedCallback() {
      this.card = this.querySelector(`.${this.classes.card}`);
      this.confetti = this.querySelector(`.${this.classes.confetti}`);
      this.counter = this.querySelector(`.${this.classes.counter}`);
      this.slots = [...this.querySelectorAll(`.${this.classes.slot}`)];
      this.badge = this.querySelector(`.${this.classes.badge}`);

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.counterEnabled = this.dataset.counterAnimation === 'true' && !this.reduceMotion;
      this.imagesEnabled = this.dataset.imagesAnimation === 'true' && !this.reduceMotion;

      if (this.counterEnabled && this.counter) this.counterEnabled = this.prepareCounter();
      if (this.imagesEnabled) this.classList.add(this.classes.animate);

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasStarted) this.start();
        });
      }, { threshold: 0.2 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.timers.forEach((timer) => clearTimeout(timer));
      clearInterval(this.swapInterval);
      cancelAnimationFrame(this.counterRaf);
    }

    later(fn, delay) {
      this.timers.push(setTimeout(fn, delay));
    }

    start() {
      this.hasStarted = true;
      this.classList.add(this.classes.inView);

      if (this.counterEnabled && this.counter) {
        this.later(() => this.runCounter(), 350);
      } else if (this.imagesEnabled) {
        this.later(() => this.popBadge(), 900);
      }

      if (this.imagesEnabled && this.slots.length > 1) {
        this.later(() => {
          this.swapFaces();
          this.swapInterval = setInterval(() => this.swapFaces(), 2200);
        }, 3200);
      }
    }

    /* ---------- Counter ---------- */
    prepareCounter() {
      this.counterTarget = parseInt(this.counter.dataset.target, 10) || 0;
      // Non-numeric values (e.g. "2M+") are left as typed
      if (this.counterTarget <= 0 || !/^[\d,.\s]+$/.test(this.counter.textContent.trim())) return false;

      this.counterStart = Math.min(parseInt(this.counter.dataset.start, 10) || 0, this.counterTarget);
      this.counterDuration = parseInt(this.counter.dataset.duration, 10) || 2200;
      this.counter.textContent = this.formatNumber(this.counterStart);
      return true;
    }

    formatNumber(value) {
      return value.toLocaleString('en-US');
    }

    runCounter() {
      let startTime = null;
      const range = this.counterTarget - this.counterStart;

      const tick = (now) => {
        if (!startTime) startTime = now;
        const progress = Math.min((now - startTime) / this.counterDuration, 1);
        const eased = 1 - Math.pow(1 - progress, 4);

        this.counter.textContent = this.formatNumber(Math.round(this.counterStart + range * eased));

        if (progress < 1) {
          this.counterRaf = requestAnimationFrame(tick);
        } else {
          this.popBadge();
        }
      };

      this.counterRaf = requestAnimationFrame(tick);
    }

    /* ---------- Images ---------- */
    popBadge() {
      if (this.imagesEnabled && this.badge) this.badge.classList.add(this.classes.pop);
      this.burstConfetti();
    }

    swapFaces() {
      const count = this.slots.length;
      this.turn = this.turn || 0;

      const a = this.turn % count;
      const b = (this.turn + 1 + (this.turn % 2)) % count;
      this.turn++;

      const srcA = this.currentSrc(this.slots[a]);
      const srcB = this.currentSrc(this.slots[b]);

      this.flipSlot(this.slots[a], srcB);
      this.later(() => this.flipSlot(this.slots[b], srcA), 180);
    }

    currentSrc(slot) {
      const flipped = slot.classList.contains(this.classes.flip);
      const img = slot.querySelector(flipped ? '[data-face="back"]' : '[data-face="front"]');
      return img.currentSrc || img.src;
    }

    flipSlot(slot, src) {
      const flipped = slot.classList.contains(this.classes.flip);
      const hidden = slot.querySelector(flipped ? '[data-face="front"]' : '[data-face="back"]');

      hidden.removeAttribute('srcset');
      hidden.src = src;

      slot.classList.toggle(this.classes.flip);
    }

    /* ---------- Confetti ---------- */
    burstConfetti() {
      if (!this.confetti || !this.badge || typeof this.confetti.burst !== 'function') return;

      const badgeRect = this.badge.getBoundingClientRect();
      const confettiRect = this.confetti.getBoundingClientRect();
      const x = badgeRect.left - confettiRect.left + badgeRect.width / 2;
      const y = badgeRect.top - confettiRect.top + badgeRect.height / 2;

      this.confetti.burst(x, y);
    }
  }

  customElements.define('wlsn-celebration-hero', WLSNCelebrationHero);
}
